import React from 'react';
import { ArrowRight, ChevronRight, ExternalLink } from 'lucide-react';
import { hasReferralUrl } from '../../config/providerRegistry';

/**
 * Shared provider card for import page list + View All Providers modal.
 * Mobile: stacked layout (logo → text). sm+: horizontal logo + details + arrow.
 */
const ProviderSelectCard = ({
  provider,
  onClick,
  variant = 'modal',
  showAffiliate = true,
}) => {
  const LogoComponent = provider.LogoComponent;
  const isModal = variant === 'modal';
  const logoSize = isModal ? 'xl' : 'lg';
  const ArrowIcon = isModal ? ChevronRight : ArrowRight;
  const arrowSize = isModal ? 20 : 16;

  const rootClass = isModal
    ? 'group relative w-full min-w-0 flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4 p-4 rounded-xl border-2 transition-all duration-200 text-left hover:shadow-md'
    : 'group relative w-full min-w-0 flex flex-col gap-2.5 sm:flex-row sm:items-start sm:gap-3 max-w-full overflow-hidden p-3 sm:p-3 rounded-xl border transition-all duration-200 text-left box-border';

  const stateClass = isModal
    ? 'border-slate-200 bg-white hover:border-emerald-400 hover:bg-emerald-50/60 dark:border-emerald-500/20 dark:bg-[#0F2A21]/90 dark:hover:border-emerald-400/55 dark:hover:bg-emerald-500/12'
    : 'border-slate-200/90 bg-white/80 hover:border-emerald-500/70 hover:bg-emerald-50/70 dark:border-emerald-500/15 dark:bg-[#0F2A21]/80 dark:hover:border-emerald-400/45 dark:hover:bg-emerald-500/10';

  const logoContainerClass = isModal
    ? 'w-full max-w-[190px] h-12 sm:w-[220px] sm:max-w-none sm:h-16 sm:shrink-0'
    : 'w-full max-w-[190px] h-12 sm:w-[180px] sm:max-w-none sm:h-14 sm:shrink-0';

  return (
    <button
      type="button"
      onClick={onClick}
      className={`${rootClass} ${stateClass}`}
      data-testid={`provider-${provider.provider_key}`}
    >
      <div className={`${logoContainerClass} flex items-center justify-start overflow-hidden`}>
        <LogoComponent size={logoSize} />
      </div>

      <div className="w-full min-w-0 flex-1 pr-7 sm:pr-0">
        <p
          className={`font-medium break-normal whitespace-normal ${
            isModal
              ? 'text-base font-semibold text-stone-900 dark:text-[#EAF7F0] sr-only'
              : 'text-sm sm:text-base leading-snug text-stone-900 dark:text-[#EAF7F0]'
          }`}
        >
          {provider.provider_name}
        </p>
        {provider.badge_text && (
          <span className="inline-flex items-center mt-1 px-2 py-0.5 rounded-full text-[10px] sm:text-xs font-medium bg-emerald-100 text-emerald-800 dark:bg-emerald-500/20 dark:text-emerald-200">
            {provider.badge_text}
          </span>
        )}
        <p className="text-xs sm:text-sm leading-relaxed mt-1 text-slate-600 dark:text-[#C7DDD0] break-normal whitespace-normal">
          {provider.provider_description}
        </p>
        {showAffiliate && hasReferralUrl(provider.provider_key) && (
          <p className="text-xs mt-1.5 flex items-center gap-1 text-emerald-700 dark:text-emerald-300/90 whitespace-nowrap">
            <ExternalLink size={10} className="shrink-0" />
            Affiliate partner
          </p>
        )}
      </div>

      <ArrowIcon
        size={arrowSize}
        className={`absolute right-3 top-1/2 -translate-y-1/2 shrink-0 pointer-events-none text-slate-400 dark:text-[#93AE9F] group-hover:text-emerald-600 dark:group-hover:text-emerald-400 transition-all sm:static sm:translate-y-0 sm:self-center ${
          isModal ? 'sm:group-hover:translate-x-0.5' : ''
        }`}
        aria-hidden
      />
    </button>
  );
};

export default ProviderSelectCard;
